import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/index';
import { setShipmentSettings } from '@/actions/common/commonPure';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import {
  AccessTimeOutlined,
  LocalShippingOutlined,
} from '@mui/icons-material';
import { i18next } from '@/config/i18n';
import { makeStyles } from '@mui/styles';
import { instance } from '@/config/axios';
import { urls } from '@/config/urls';
import { useToast } from '../Dashboard/ToastContext';

interface TimeWindow {
  start: string | null;
  end: string | null;
}

interface ShipmentSettingsData {
  pickup_time_window: TimeWindow;
  delivery_time_window: TimeWindow;
}

type SectionKey = keyof ShipmentSettingsData;

const useStyles = makeStyles(() => ({
  Outerspacing: {
    margin: '20px 0px',
    height: '91%',
  },
}));

const TIME_WINDOW_KEYS: SectionKey[] = ['pickup_time_window', 'delivery_time_window'];
const formatTimeToHHMMSS = (date: Date | null): string | null => {
  if (!date) return null;
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const parseLocalTime = (timeStr: string | null): Date | null => {
  if (!timeStr) return null;
  const [time] = timeStr.split('.');
  const parts = time.split(':');
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  const s = parseInt(parts[2], 10) || 0;
  const d = new Date();
  d.setHours(h, m, s, 0);
  return d;
};

const isValidRange = (start: string | null, end: string | null): boolean => {
  if (!start || !end) return true;
  return start < end;
};

const TimeWindowRow: React.FC<{
  section: SectionKey;
  settings: ShipmentSettingsData;
  onChange: (
    section: SectionKey,
    key: 'start' | 'end',
    value: Date | null
  ) => void;
}> = ({ section, settings, onChange }) => {
  const IconComponent =
    section === 'pickup_time_window' ? AccessTimeOutlined : LocalShippingOutlined;

  const startTime = parseLocalTime(settings[section]?.start ?? null);
  const endTime = parseLocalTime(settings[section]?.end ?? null);

  return (
    <TableRow hover sx={{ borderBottom: '1px solid #e0e0e0' }}>
      <TableCell sx={{ py: 1.5 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconComponent fontSize="small" />
          <Typography variant="subtitle1">
            {i18next.t(
              `common:settings.${section === 'pickup_time_window' ? 'pickupWindow' : 'deliveryWindow'}`
            )}
          </Typography>
        </Box>
      </TableCell>

      <TableCell sx={{ py: 1.5 }}>
        <TimePicker
          label={i18next.t('common:settings.starttime')}
          value={startTime}
          onChange={(newValue) => onChange(section, 'start', newValue)}
          views={['hours', 'minutes']}
          slotProps={{
            textField: {
              fullWidth: true,
              margin: 'none',
            },
          }}
        />
      </TableCell>

      <TableCell sx={{ py: 1.5 }}>
        <TimePicker
          label={i18next.t('common:settings.endtime')}
          value={endTime}
          onChange={(newValue) => onChange(section, 'end', newValue)}
          views={['hours', 'minutes']}
          slotProps={{
            textField: {
              fullWidth: true,
              margin: 'none',
            },
          }}
        />
      </TableCell>
    </TableRow>
  );
};

const ShipmentSettings: React.FC = () => {
  const dispatch = useDispatch();
  const { success, error } = useToast();
  const [settings, setSettings] = useState<ShipmentSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const { server } = urls;
  const classes = useStyles();
  const DEFAULT_TW: TimeWindow = { start: null, end: null };

  const normalizeSettings = (data: Partial<ShipmentSettingsData> | null | undefined): ShipmentSettingsData => ({
    pickup_time_window: data?.pickup_time_window ?? { ...DEFAULT_TW },
    delivery_time_window: data?.delivery_time_window ?? { ...DEFAULT_TW },
  });

  useEffect(() => {
    instance
      .get(server.settings.shipments)
      .then((response) => {
        const normalized = normalizeSettings(response.data);
        dispatch(setShipmentSettings(normalized));
        setSettings(normalized);
        setLoading(false);
      })
      .catch(() => {
        setFetchError('Failed to fetch settings');
        setLoading(false);
      });
  }, [dispatch]);

  const validateTimes = (s: ShipmentSettingsData): boolean => {
    const { pickup_time_window, delivery_time_window } = s;
    const isPickupValid = isValidRange(pickup_time_window.start, pickup_time_window.end);
    const isDeliveryValid = isValidRange(delivery_time_window.start, delivery_time_window.end);

    if (!isPickupValid || !isDeliveryValid) {
      error(
        <>
          <b>{i18next.t('common:toast.errorTitle')}:</b>&nbsp;
          {i18next.t('common:toast.startBeforeEnd')}
        </>
      );
      return false;
    }
    return true;
  };

  const handleChange = (
    section: SectionKey,
    key: 'start' | 'end',
    value: Date | null
  ) => {
    if (!settings) return;
    const updatedSettings: ShipmentSettingsData = {
      ...settings,
      [section]: {
        ...settings[section],
        [key]: formatTimeToHHMMSS(value),
      },
    };
    setSettings(updatedSettings);
    validateTimes(updatedSettings);
  };

  const handleSave = () => {
    if (!settings || !validateTimes(settings)) return;

    setSaving(true);
    instance
      .patch(server.settings.shipments, settings)
      .then(() => {
        success(
          <>
            <b>{i18next.t('common:toast.successTitle')}:</b>&nbsp;
            {i18next.t('common:toast.saveSuccess')}
          </>
        );
      })
      .catch(() => {
        error(
          <>
            <b>{i18next.t('common:toast.errorTitle')}:</b>&nbsp;
            {i18next.t('common:toast.saveError')}
          </>
        );
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Box className={classes.Outerspacing}>
      {loading ? (
        <CircularProgress />
      ) : fetchError ? (
        <Alert severity="error">{fetchError}</Alert>
      ) : (
        <>
          <TableContainer
            component={Box}
            sx={{
              mt: 2,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              backgroundColor: 'white',
            }}
          >
            <Table>
              <TableBody>
                {TIME_WINDOW_KEYS.map((key) => (
                  <TimeWindowRow
                    key={key}
                    section={key}
                    settings={settings!}
                    onChange={handleChange}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving
                ? i18next.t('common:settings.saving')
                : i18next.t('common:settings.save')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ShipmentSettings;
